const REACTION_ICONS = {
  heart: 'heart',
  joy: 'laugh',
  sob: 'cry',
  '-1': 'hate',
};

function decodeEntities(value) {
  const named = {
    amp: '&',
    apos: "'",
    gt: '>',
    lt: '<',
    nbsp: ' ',
    quot: '"',
    zwj: '\u200d',
    zwnj: '\u200c',
  };

  return value.replace(/&(#x[\da-f]+|#\d+|[a-z]+);/gi, (entity, code) => {
    if (code[0] !== '#') return named[code.toLowerCase()] ?? entity;
    const radix = code[1].toLowerCase() === 'x' ? 16 : 10;
    const valueStart = radix === 16 ? 2 : 1;
    const codePoint = Number.parseInt(code.slice(valueStart), radix);
    return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : entity;
  });
}

function commentText(cooked) {
  if (typeof cooked !== 'string') return '';

  return decodeEntities(cooked)
    .replace(/<(aside|script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<br\s*\/?\s*>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function eligibleReactions(reactions) {
  if (!Array.isArray(reactions)) return [];

  return reactions.flatMap((reaction) => {
    const icon = REACTION_ICONS[reaction?.emoji];
    const count = Number(reaction?.count);
    if (!icon || !Number.isFinite(count) || count <= 1) return [];
    return [{ emoji: reaction.emoji, icon, count }];
  });
}

// Moderators receive soft-deleted messages; nobody else may see or count them.
function isDeletedChannelMessage(message) {
  return Boolean(message?.deleted_at);
}

function selectEligibleChartComments(messages, now = Date.now(), seenIds = new Set()) {
  if (!Array.isArray(messages)) return [];
  const fourHoursAgo = now - 4 * 60 * 60 * 1000;

  return [...messages].sort((first, second) => {
    return Date.parse(second?.created_at) - Date.parse(first?.created_at);
  }).flatMap((message) => {
    const createdAt = Date.parse(message?.created_at);
    const text = commentText(message?.cooked);
    const id = message?.id;
    const isReply = Boolean(message?.in_reply_to?.id);
    const isRecent = Number.isFinite(createdAt) && createdAt >= fourHoursAgo && createdAt <= now;
    const validLength = Array.from(text).length >= 1 && Array.from(text).length <= 60;

    if (id === undefined || id === null || seenIds.has(id) || isReply || isDeletedChannelMessage(message) || !isRecent || !validLength) return [];

    let avatar = message?.user?.avatar?.['144'] || '';
    if (avatar.startsWith('https://hub.arzdigital.comhttps://cdn')) {
      avatar = avatar.replace('https://hub.arzdigital.com', '');
    }

    return [{
      id,
      text,
      avatar,
      author: message?.user?.name || '',
      reactions: eligibleReactions(message?.reactions),
    }];
  });
}

function seededRandom(seed) {
  let value = seed >>> 0;
  return () => {
    value += 0x6D2B79F5;
    let result = value;
    result = Math.imul(result ^ (result >>> 15), result | 1);
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61);
    return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
  };
}

function hashSeed(value) {
  return Array.from(String(value)).reduce((hash, character) => {
    return Math.imul(hash ^ character.codePointAt(0), 16777619);
  }, 2166136261) >>> 0;
}

function createReactionParticles(commentId, reactions) {
  if (!Array.isArray(reactions)) return [];
  const sides = ['top', 'right', 'bottom'];
  const particles = [];
  const plans = reactions.map((reaction, reactionIndex) => ({
    reaction,
    random: seededRandom(hashSeed(`${commentId}:${reaction.emoji}:${reactionIndex}`)),
    amount: Math.min(3, Math.max(1, Math.ceil((reaction.count - 1) / 10))),
  }));

  for (let round = 0; round < 3 && particles.length < 6; round += 1) {
    for (const plan of plans) {
      if (round >= plan.amount || particles.length >= 6) continue;
      const { reaction, random } = plan;
      const side = sides[(particles.length + Math.floor(random() * sides.length)) % sides.length];
      particles.push({
        icon: reaction.icon,
        side,
        offset: Number((18 + random() * 70).toFixed(2)),
        opacity: Number((0.22 + random() * 0.36).toFixed(2)),
        delay: Math.round(random() * 180),
        rotation: Math.round(-12 + random() * 24),
      });
    }
  }

  return particles;
}

function createBubblePosition(commentId) {
  const random = seededRandom(hashSeed(`position:${commentId}`));
  return {
    x: Number((4 + random() * 40).toFixed(2)),
    y: Number((18 + random() * 50).toFixed(2)),
  };
}

function clampBubblePosition(position, overlaySize, bubbleSize, inset = 10) {
  const desiredX = overlaySize.width * position.x / 100;
  const desiredY = overlaySize.height * position.y / 100;
  const maxX = Math.max(inset, overlaySize.width - bubbleSize.width - inset);
  const maxY = Math.max(inset, overlaySize.height - bubbleSize.height - inset);

  return {
    x: Number(Math.min(maxX, Math.max(inset, desiredX)).toFixed(2)),
    y: Number(Math.min(maxY, Math.max(inset, desiredY)).toFixed(2)),
  };
}

function getCommentLayout(text) {
  const characterCount = Array.from(typeof text === 'string' ? text : '').length;
  if (characterCount <= 16) return 'short';
  if (characterCount <= 36) return 'medium';
  return 'long';
}

function rectDistance(rect, point) {
  const dx = Math.max(rect.left - point.x, 0, point.x - rect.right);
  const dy = Math.max(rect.top - point.y, 0, point.y - rect.bottom);
  return Math.hypot(dx, dy);
}

function rectsOverlap(first, second, gap = 0) {
  return first.left < second.right + gap
    && first.right > second.left - gap
    && first.top < second.bottom + gap
    && first.bottom > second.top - gap;
}

// Picks the spot that keeps the bubble off the price line and chart labels,
// using the seeded preferred position to vary placement between comments.
function chooseBubblePlacement(options) {
  const {
    overlay,
    bubble,
    preferred,
    line = [],
    obstacles = [],
    softObstacles = [],
    previous = null,
    inset = 10,
    gap = 10,
  } = options;
  const maxX = Math.max(inset, overlay.width - bubble.width - inset);
  const maxY = Math.max(inset, overlay.height - bubble.height - inset);
  const steps = (max, minStep, count) => {
    const total = Math.max(1, Math.min(count, Math.floor((max - inset) / minStep)));
    return Array.from({ length: total + 1 }, (_, index) => inset + (max - inset) * index / total);
  };
  const target = clampBubblePosition(preferred, overlay, bubble, inset);
  const diagonal = Math.hypot(overlay.width, overlay.height) || 1;
  let best = null;

  for (const y of steps(maxY, 6, 16)) {
    for (const x of steps(maxX, 8, 24)) {
      const rect = { left: x, top: y, right: x + bubble.width, bottom: y + bubble.height };
      let clearance = 56;
      for (const point of line) {
        if (point.x < rect.left - 56 || point.x > rect.right + 56) continue;
        clearance = Math.min(clearance, rectDistance(rect, point));
      }

      let score = clearance < gap ? clearance * 10 - 1000 : clearance;
      for (const obstacle of obstacles) {
        if (rectsOverlap(rect, obstacle, gap)) score -= 5000;
      }
      for (const obstacle of softObstacles) {
        if (rectsOverlap(rect, obstacle)) score -= 40;
      }
      score -= Math.hypot(x - target.x, y - target.y) / diagonal * 30;
      if (previous && Math.hypot(x - previous.x, y - previous.y) < 80) score -= 25;

      if (!best || score > best.score) best = { x, y, score };
    }
  }

  return { x: Number(best.x.toFixed(2)), y: Number(best.y.toFixed(2)) };
}

function isPlaybackBlocked(activity) {
  return Boolean(
    activity?.documentHidden
    || activity?.chartVisible === false
    || activity?.pointerInside
    || activity?.pointerDown
    || activity?.selecting
  );
}

function releaseChartInteraction(activity, pointerType = '') {
  if (!activity) return activity;
  activity.pointerDown = false;
  activity.selecting = false;
  activity.tooltipVisible = false;
  if (pointerType && pointerType !== 'mouse') activity.pointerInside = false;
  return activity;
}

function scheduleInitialChannelMessagesRequest(request, setTimer = setTimeout) {
  return setTimer(request, 2000);
}

function skipInterruptedChartComment(comment, seenIds, queuedIds) {
  if (!comment) return false;
  queuedIds.delete(comment.id);
  seenIds.add(comment.id);
  return true;
}

function selectLatestChannelMessages(messages, limit) {
  if (!Array.isArray(messages)) return [];
  const pageSize = Math.max(0, Math.floor(Number(limit) || 0));
  if (pageSize === 0) return [];
  return messages.slice(-pageSize);
}

function createChannelMessagesCoordinator(options = {}) {
  const fetchMessages = options.fetchMessages;
  const now = options.now || Date.now;
  const setTimer = options.setTimer || setTimeout;
  const clearTimer = options.clearTimer || clearTimeout;
  const refreshInterval = options.refreshInterval || 5 * 60_000;
  const retryDelay = options.retryDelay || 15_000;
  const channels = new Map();

  const getEntry = (channelId) => {
    const key = String(channelId);
    if (!channels.has(key)) {
      channels.set(key, {
        channelId: key,
        consumers: new Set(),
        subscribers: new Set(),
        latest: null,
        fetchedAt: 0,
        promise: null,
        timer: null,
      });
    }
    return channels.get(key);
  };

  const clearEntryTimer = (entry) => {
    if (entry.timer === null) return;
    clearTimer(entry.timer);
    entry.timer = null;
  };

  const schedule = (entry, delay) => {
    clearEntryTimer(entry);
    if (entry.consumers.size === 0) return;
    entry.timer = setTimer(() => {
      entry.timer = null;
      request(entry.channelId, { force: true });
    }, delay);
  };

  const publish = (channelId, body) => {
    if (!body || !Array.isArray(body.data)) return null;
    const visibleMessages = body.data.filter((message) => !isDeletedChannelMessage(message));
    if (visibleMessages.length !== body.data.length) body = { ...body, data: visibleMessages };
    const entry = getEntry(channelId);
    entry.latest = body;
    entry.fetchedAt = now();
    for (const subscriber of entry.subscribers) {
      try {
        subscriber(body);
      } catch (_error) {
        // One optional consumer must not prevent the other from receiving data.
      }
    }
    schedule(entry, refreshInterval);
    return body;
  };

  const request = (channelId, requestOptions = {}) => {
    const entry = getEntry(channelId);
    const isFresh = entry.latest && now() - entry.fetchedAt < refreshInterval;
    if (!requestOptions.force && isFresh) return Promise.resolve(entry.latest);
    if (entry.promise) return entry.promise;
    if (typeof fetchMessages !== 'function') return Promise.resolve(null);

    entry.promise = Promise.resolve()
      .then(() => fetchMessages(entry.channelId))
      .then((body) => publish(entry.channelId, body))
      .catch(() => {
        schedule(entry, retryDelay);
        return null;
      })
      .finally(() => {
        entry.promise = null;
      });
    return entry.promise;
  };

  const refresh = (channelId) => request(channelId, { force: true });

  const subscribe = (channelId, subscriber) => {
    const entry = getEntry(channelId);
    entry.subscribers.add(subscriber);
    return () => entry.subscribers.delete(subscriber);
  };

  const setDemand = (channelId, consumerId, active) => {
    const entry = getEntry(channelId);
    if (active) entry.consumers.add(String(consumerId));
    else entry.consumers.delete(String(consumerId));

    if (entry.consumers.size === 0) clearEntryTimer(entry);
    else if (entry.latest && entry.timer === null) {
      const elapsed = Math.max(0, now() - entry.fetchedAt);
      schedule(entry, Math.max(0, refreshInterval - elapsed));
    }
  };

  const peek = (channelId) => getEntry(channelId);

  return {
    peek,
    publish,
    refresh,
    request,
    selectLatestChannelMessages,
    setDemand,
    subscribe,
  };
}

function getCommentStandingTime(text) {
  const characterCount = Array.from(typeof text === 'string' ? text : '').length;
  return 2800 + Math.max(0, characterCount - 35) * 60;
}

function getRemainingInitialDelay(chartReadyAt, now = Date.now()) {
  return Math.max(0, 3000 - Math.max(0, now - chartReadyAt));
}

function getInteractionResumeDelay() {
  return 1500;
}

function createChannelMessagesRequest(baseUrl, channelId, authenticated, authHeaders = {}) {
  const visibility = authenticated ? '' : 'pub/';
  return {
    url: `${baseUrl}/v1/${visibility}channels/${encodeURIComponent(channelId)}/messages?page_size=30`,
    headers: { ...authHeaders, Accept: 'application/json' },
  };
}

module.exports = {
  chooseBubblePlacement,
  clampBubblePosition,
  createChannelMessagesCoordinator,
  createChannelMessagesRequest,
  createBubblePosition,
  createReactionParticles,
  getCommentLayout,
  getCommentStandingTime,
  getInteractionResumeDelay,
  getRemainingInitialDelay,
  isDeletedChannelMessage,
  isPlaybackBlocked,
  releaseChartInteraction,
  scheduleInitialChannelMessagesRequest,
  selectLatestChannelMessages,
  selectEligibleChartComments,
  skipInterruptedChartComment,
};
